import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, ArrowRight, Sparkles } from "lucide-react";

const TIER_BADGE = {
  basic: { label: "Foundation", cls: "bg-[#00FF9D]/15 text-[#00FF9D] border-[#00FF9D]/30" },
  intermediate: { label: "Practitioner", cls: "bg-[#0044FF]/15 text-[#8aa8ff] border-[#0044FF]/30" },
  expert: { label: "Expert", cls: "bg-[#5D3FD3]/15 text-[#b9a8ff] border-[#5D3FD3]/30" },
};

/**
 * Peer Lookalike row — shows up to 3 paths that learners with the same
 * domain or seniority signal recently studied. Hidden cleanly when the
 * user hasn't filled their persona yet (no signal → no value, just noise).
 * Paths are anonymized server-side; we never expose peer identities.
 */
export default function PeerLookalikeRow() {
  const [state, setState] = useState({ loading: true, recs: [], reason: null });

  useEffect(() => {
    let cancelled = false;
    api.get("/peers/lookalike")
      .then(({ data }) => {
        if (!cancelled) setState({
          loading: false,
          recs: data?.recommendations || [],
          reason: data?.reason || null,
          peers: data?.peers_found || 0,
          signals: data?.match_signals || {},
        });
      })
      .catch(() => { if (!cancelled) setState({ loading: false, recs: [], reason: "error" }); });
    return () => { cancelled = true; };
  }, []);

  // Hide the entire row when persona is empty or no peers exist — no
  // empty-state noise on a fresh dashboard.
  if (state.loading || state.recs.length === 0) return null;

  return (
    <section className="space-y-3" data-testid="peer-lookalike-row">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users size={14} className="text-[#8aa8ff]" />
          <h3 className="font-display text-base md:text-lg text-white tracking-tight">
            Peers like you are studying
          </h3>
          {state.peers > 0 && (
            <span className="text-[11px] text-white/45 font-mono-oak" data-testid="peer-lookalike-count">
              {state.peers} match{state.peers === 1 ? "" : "es"}
            </span>
          )}
        </div>
        <Link
          to="/settings"
          className="text-[11px] text-white/45 hover:text-white transition inline-flex items-center gap-1"
          data-testid="peer-lookalike-tune"
        >
          <Sparkles size={11} /> Tune persona
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="peer-lookalike-grid">
        {state.recs.map((p, i) => {
          const tier = TIER_BADGE[p.difficulty_tier] || TIER_BADGE.intermediate;
          return (
            <article
              key={p.path_id || i}
              className="rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-[#0044FF]/40 transition p-4 flex flex-col gap-3 animate-fadeup"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`peer-lookalike-card-${i}`}
            >
              <div className="flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-mono-oak ${tier.cls}`}>
                  {tier.label}
                </span>
                <span className="text-[10px] text-white/45">peer pick</span>
              </div>
              <div className="font-display text-[15px] text-white leading-snug min-h-[44px]">
                {p.goal}
              </div>
              {p.summary && (
                <p className="text-[12px] text-white/55 leading-relaxed line-clamp-2">{p.summary}</p>
              )}
              <Link
                to={`/path/${p.path_id}`}
                className="mt-auto inline-flex items-center gap-1.5 text-xs text-[#8aa8ff] hover:text-white transition"
                data-testid={`peer-lookalike-open-${i}`}
              >
                Explore path <ArrowRight size={12} />
              </Link>
            </article>
          );
        })}
      </div>
    </section>
  );
}
